import { useCallback, useMemo, useState } from 'react';
import type { SignInMethodsAnswer, SignInProvider } from 'fym-shared/src/music24/signInMethods';
import { config } from '../../../../../config/config';
import { useScript } from '../../../../../utils/hooks/useScript';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../../trpc/trpc';
import { usePreparedLink } from './usePreparedLink';

const GIS_URL = 'https://accounts.google.com/gsi/client';
const APPLE_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/** `error` is a server error or a `ClientLinkFailure` ('cancelled', 'unavailable'). */
export type SignInMethodsFailure = { error: unknown; provider: SignInProvider | null } | null;

/**
 * Settings → Account → Sign-in methods: the list (`signInMethods.list`), Connect in link mode
 * (`startLink` + the provider popup + `link`) and Disconnect (`disconnect`).
 *
 * `canConnect` is false in the native web view: the provider popups do not work there.
 */
export function useSignInMethods({ canConnect }: { canConnect: boolean }) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const query = useTRPCQuery(trpcAPI.signInMethods.list.queryOptions(undefined, { retry: false }));
  const data = query.data as SignInMethodsAnswer | undefined;
  const { refetch: refetchUnsafe } = query;
  const refetch = useCallback(() => {
    refetchUnsafe().catch(console.error);
  }, [refetchUnsafe]);

  // A provider can be connected when the web app and the server have its keys and the user has
  // no identity of it yet.
  const connectable = useMemo(() => {
    const connected = new Set(data?.identities.map((identity) => identity.provider) ?? []);
    const available = data?.availableProviders ?? [];
    const isOpen = (provider: SignInProvider, clientId: string): boolean =>
      canConnect && clientId !== '' && available.includes(provider) && !connected.has(provider);
    return { google: isOpen('google', config.googleClientId), apple: isOpen('apple', config.appleServicesId) };
  }, [canConnect, data]);
  const googleOn = connectable.google;
  const appleOn = connectable.apple;

  useScript(googleOn ? GIS_URL : undefined);
  useScript(appleOn ? APPLE_URL : undefined);
  const googleLink = usePreparedLink('google', googleOn);
  const appleLink = usePreparedLink('apple', appleOn);

  const [failure, setFailure] = useState<SignInMethodsFailure>(null);
  const [linkedProvider, setLinkedProvider] = useState<SignInProvider | null>(null);
  const [busyProvider, setBusyProvider] = useState<SignInProvider | null>(null);

  const fail = useCallback((provider: SignInProvider | null, error: unknown) => {
    setFailure({ provider, error });
    setBusyProvider(null);
  }, []);

  const finishLink = useCallback(
    async (provider: SignInProvider, send: () => Promise<unknown>) => {
      try {
        await send();
        setLinkedProvider(provider);
        setFailure(null);
        await refetchUnsafe();
      } catch (error) {
        fail(provider, error);
        return;
      }
      setBusyProvider(null);
    },
    [fail, refetchUnsafe]
  );

  const connectGoogle = useCallback(() => {
    const oauth2 = window.google?.accounts?.oauth2;
    const prepared = googleLink.take();
    if (!oauth2 || !prepared) {
      fail('google', 'unavailable');
      return;
    }
    setFailure(null);
    setLinkedProvider(null);
    setBusyProvider('google');
    const codeClient = oauth2.initCodeClient({
      client_id: config.googleClientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      state: prepared.state,
      callback: (response) => {
        googleLink.refresh();
        if (response.state !== prepared.state || !response.code) {
          fail('google', 'cancelled');
          return;
        }
        const authCode = response.code;
        finishLink('google', () =>
          trpcClient.signInMethods.link.mutate({ provider: 'google', state: prepared.state, authCode, client: 'web' })
        ).catch(console.error);
      },
    });
    codeClient.requestCode();
  }, [fail, finishLink, googleLink, trpcClient]);

  // Do not await anything before signIn(). The browser blocks a popup that does not open inside
  // the click handler. So the state and the nonce are fetched before the click.
  const connectApple = useCallback(() => {
    const appleId = window.AppleID;
    const prepared = appleLink.take();
    if (!appleId || !prepared) {
      fail('apple', 'unavailable');
      return;
    }
    setFailure(null);
    setLinkedProvider(null);
    setBusyProvider('apple');
    const signIn = async (): Promise<{ id_token: string; code: string; state?: string } | null> => {
      try {
        appleId.auth.init({
          clientId: config.appleServicesId,
          scope: 'name email',
          redirectURI: config.appleRedirectUri,
          usePopup: true,
          nonce: prepared.hashedNonce,
          state: prepared.state,
        });
        const res = await appleId.auth.signIn();
        return res.authorization;
      } catch {
        return null;
      } finally {
        // The state and the nonce are single-use. Get new ones after every attempt.
        appleLink.refresh();
      }
    };
    const attempt = async (): Promise<void> => {
      const authorization = await signIn();
      if (authorization === null || (authorization.state !== undefined && authorization.state !== prepared.state)) {
        fail('apple', 'cancelled');
        return;
      }
      await finishLink('apple', () =>
        trpcClient.signInMethods.link.mutate({
          provider: 'apple',
          state: prepared.state,
          idToken: authorization.id_token,
          code: authorization.code,
          nonce: prepared.rawNonce,
          client: 'web',
        })
      );
    };
    attempt().catch(console.error);
  }, [appleLink, fail, finishLink, trpcClient]);

  const connect = useCallback(
    (provider: SignInProvider) => (provider === 'apple' ? connectApple() : connectGoogle()),
    [connectApple, connectGoogle]
  );

  const disconnectMutation = useTRPCMutation({
    mutationFn: (identityId: number) => trpcClient.signInMethods.disconnect.mutate({ identityId }),
    onSuccess: () => {
      setFailure(null);
      refetch();
    },
    onError: (error) => {
      setFailure({ provider: null, error });
      refetch();
    },
  });

  const disconnect = useCallback(
    (identityId: number) => {
      setLinkedProvider(null);
      disconnectMutation.mutate(identityId);
    },
    [disconnectMutation]
  );

  return {
    data,
    isLoading: query.isLoading,
    error: query.error,
    refetch,
    canConnectProvider: (provider: SignInProvider): boolean => (provider === 'apple' ? appleOn : googleOn),
    isProviderReady: (provider: SignInProvider): boolean => (provider === 'apple' ? appleLink.ready : googleLink.ready),
    connect,
    busyProvider,
    disconnect,
    isDisconnecting: disconnectMutation.isPending,
    failure,
    linkedProvider,
  };
}
